/* ════════════ THE INGEST ════════════
   The NSA page's atmosphere: data dashes stream in from every
   edge of the screen and vanish into a dark block near the
   corner. The block is the datacenter. It is never full. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function ingest(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#ingest")!;
  const ctx = c.getContext("2d")!;

  const CYAN = "#36c9ff";
  const GREY = "#9aa0a6";

  // the datacenter: a dark slab near the bottom-right corner
  let box = { x: 0, y: 0, w: 0, h: 0 };

  type Dash = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    len: number;
    col: string;
  };
  let dashes: Dash[] = [];

  const spawn = (): Dash => {
    // spawn on a random edge, aim at a random point inside the box
    const edge = Math.floor(Math.random() * 4);
    let x = 0;
    let y = 0;
    if (edge === 0) { x = Math.random() * c.width; y = -8; }
    else if (edge === 1) { x = Math.random() * c.width; y = c.height + 8; }
    else if (edge === 2) { x = -8; y = Math.random() * c.height; }
    else { x = c.width + 8; y = Math.random() * c.height; }
    const tx = box.x + Math.random() * box.w;
    const ty = box.y + Math.random() * box.h;
    const d = Math.hypot(tx - x, ty - y) || 1;
    const speed = 0.6 + Math.random() * 1.1;
    const isHot = Math.random() < 0.18;
    return {
      x,
      y,
      vx: ((tx - x) / d) * speed,
      vy: ((ty - y) / d) * speed,
      len: 5 + Math.random() * 7,
      col: isHot ? CYAN : GREY,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    box = {
      x: c.width * 0.8,
      y: c.height * 0.78,
      w: Math.min(150, c.width * 0.16),
      h: Math.min(84, c.height * 0.12),
    };
    const count = Math.min(60, Math.floor((c.width * c.height) / 28000));
    dashes = Array.from({ length: count }, () => {
      const d = spawn();
      // desync: scatter along the inbound path
      const t = Math.random() * 400;
      d.x += d.vx * t;
      d.y += d.vy * t;
      return d;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const drawBox = () => {
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = "rgba(54, 201, 255, 0.05)";
    ctx.fillRect(box.x, box.y, box.w, box.h);
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = GREY;
    ctx.lineWidth = 1;
    ctx.strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);
    // one status light. always on. of course it's always on.
    ctx.globalAlpha = 0.6;
    ctx.fillStyle = CYAN;
    ctx.fillRect(box.x + 6, box.y + 6, 2, 2);
  };

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    drawBox();
    for (const d of dashes) {
      const hot = d.col === CYAN;
      ctx.globalAlpha = hot ? 0.45 : 0.18;
      ctx.strokeStyle = d.col;
      ctx.lineWidth = 1;
      const m = d.len / Math.hypot(d.vx, d.vy);
      ctx.beginPath();
      ctx.moveTo(d.x, d.y);
      ctx.lineTo(d.x - d.vx * m, d.y - d.vy * m);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < dashes.length; i++) {
      const d = dashes[i]!;
      d.x += d.vx;
      d.y += d.vy;
      // swallowed by the datacenter: respawn at an edge
      if (
        d.x > box.x && d.x < box.x + box.w &&
        d.y > box.y && d.y < box.y + box.h
      ) {
        dashes[i] = spawn();
      }
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(ingest);
